/* eslint-disable @typescript-eslint/no-unused-vars */
import { delay } from '@/utils'
import { defineStore } from 'pinia'
import { computed, onMounted, onUnmounted, reactive, ref } from 'vue'

const debug =
  location.hostname.startsWith('192.') || location.hostname.startsWith('10.')

type Snapshot = {
  base64?: string
  width?: string
  height?: string
}

type TryOnTarget = 'single-item' | 'stacking' | undefined
type TryOnAttr = 'facial' | 'hand' | undefined

export const useCameraFrameStore = defineStore('camera-frame', () => {
  const tryOnInstance = ref<HTMLIFrameElement>()
  const defaultInstance = ref<HTMLIFrameElement>()

  const cameraMode = ref<'none' | 'idle' | 'try-on' | 'default' | 'both'>(
    'none'
  )

  const isTryOnCameraIdle = ref<boolean>(true)
  const isTryOnCameraReady = ref<boolean>(debug || false)
  const isTryOnCameraError = ref<boolean>(false)
  const isTryOnCameraFailed = ref<boolean>(false)

  const isDefaultCameraReady = ref<boolean>(debug || false)
  const isDefaultCameraFailed = ref<boolean>(false)

  const tryOnType = reactive<{ target: TryOnTarget; attr: TryOnAttr }>({
    target: undefined,
    attr: undefined
  })
  const pendingSkuId = ref<string>()
  const applyingSkuId = ref<string>()
  const currentSkuId = ref<string>()
  const appliedSkuids = ref<string[]>([])
  const snapshotResult = ref<Snapshot>({})

  const tryOnUrlVersion = ref(124)
  const tryOnUrl = computed(() =>
    tryOnType.target && tryOnType.attr && !hasRejectedEver.value
      ? `./iframe/${tryOnType.attr}-${tryOnType.target}.html?${process.env.VUE_APP_SDK_TYPE}=${tryOnUrlVersion.value}`
      : ''
  )

  const isSkuLoading = computed(
    () =>
      applyingSkuId.value !== undefined &&
      applyingSkuId.value !== '' &&
      !appliedSkuids.value.includes(applyingSkuId.value)
  )

  const isTryOnCamerasShown = computed(() =>
    ['try-on', 'both'].includes(cameraMode.value)
  )
  const isTryOnCameraLoading = computed(
    () =>
      isTryOnCamerasShown.value &&
      !isTryOnCameraReady.value &&
      !isTryOnCameraFailed.value
  )
  const isDefaultCameraShown = computed(() =>
    ['default', 'both'].includes(cameraMode.value)
  )
  const isDefaultCameraActive = computed(() =>
    ['idle', 'default', 'both'].includes(cameraMode.value)
  )
  const isDefaultCameraLoading = computed(
    () =>
      isDefaultCameraShown.value &&
      !isDefaultCameraReady.value &&
      !isDefaultCameraFailed.value
  )

  const isTryOnCameraMount = computed(
    () => isTryOnCameraReady.value || isTryOnCameraFailed.value
  )

  const hasUnreadCameraFailed = computed(
    () => isTryOnCameraFailed.value || isDefaultCameraFailed.value
  )

  const isCameraLoading = computed(() => {
    console.log('[[ Layout ] Camera State ]', {
      mode: cameraMode.value,
      tryonShown: isTryOnCamerasShown.value,
      tryonReady: isTryOnCameraReady.value,
      tryonFailed: isTryOnCameraFailed.value,
      defaultShown: isDefaultCameraShown.value,
      defaultReady: isDefaultCameraReady.value,
      defaultFailed: isDefaultCameraFailed.value
    })
    return isTryOnCameraLoading.value || isDefaultCameraLoading.value
  })

  const resumeCamera = async () => {
    // console.log('摄像头开始工作')
    await delay(50)
    if (tryOnInstance.value && isTryOnCameraIdle.value) {
      isTryOnCameraIdle.value = false
      tryOnInstance.value.contentWindow?.postMessage({
        play: true
      })
    }
    await delay(0)
  }
  const pauseCamera = async () => {
    // console.log('摄像头暂停工作', offset)
    if (tryOnInstance.value && !isTryOnCameraIdle.value) {
      isTryOnCameraIdle.value = true
      tryOnInstance.value.contentWindow?.postMessage({
        pause: true
      })
    }
    await delay(0)
  }
  const isTryOnCameraStopped = ref(false)
  const stopCamera = async () => {
    await pauseCamera()
    isTryOnCameraStopped.value = true
  }
  const startCamera = async () => {
    // console.log('摄像头工作', tryOnInstance.value, isTryOnCameraStopped.value)
    if (tryOnInstance.value) {
      if (isTryOnCameraStopped.value) {
        tryOnInstance.value.contentWindow?.postMessage({
          reload: true
        })

        pendingSkuId.value =
          pendingSkuId.value || applyingSkuId.value || currentSkuId.value
        resetPartialStates()

        isTryOnCameraStopped.value = false
      } else {
        resumeCamera()
      }
    }
    await delay(0)
  }

  const snapshot = () => {
    if (tryOnInstance.value) {
      isTryOnCameraError.value = false
      snapshotResult.value = {}
      tryOnInstance.value.contentWindow?.postMessage({ snapshot: true })
    }
  }

  const setSnapshotResult = (payload: Snapshot) => {
    if (snapshotResult.value.base64) return
    isTryOnCameraError.value = false
    snapshotResult.value = payload
  }

  const markFailed = async (wait: number) => {
    hasRejectedEver.value = true
    await delay(wait)
    isTryOnCameraFailed.value = true
    isDefaultCameraFailed.value = true
  }

  const getUserMedia = (fromTryOn = false) =>
    new Promise<MediaStream>((resolve, reject) => {
      if (hasRejectedEver.value) {
        markFailed(800).then(() => reject('hasRejectedEver'))
      } else {
        navigator.mediaDevices
          ?.getUserMedia({ video: { facingMode: 'user' } })
          .then(mediaStream => {
            // console.log('[[ Camera ] Allowed ]')
            // hasRejectedEver.value = false
            resolve(mediaStream)
          })
          .catch(error => {
            markFailed(0)
            reject(error)
          })
      }
    })

  const setTryOnType = async (target?: TryOnTarget, attr?: TryOnAttr) => {
    // console.log('[[ Camera ] SetTryOnType ]', { attr, target, debug })
    if (debug) return
    if (target && attr) {
      if (hasRejectedEver.value) return markFailed(500)
      if (target === tryOnType.target && attr === tryOnType.attr) return
      resetPartialStates()
      // try {
      //   console.error('[[ Camera ] Request Permission By Script ]')
      //   await getUserMedia(true)
      // } catch (error) {
      //   console.error('[[ Camera ] Request Permission Failed ]')
      // }
    }
    Object.assign(tryOnType, { target, attr })
  }

  const postSkuId = (reason: string) => {
    if (debug) return
    // console.log(
    //   '[[ Camera ] apply / try to post item',
    //   {
    //     reason,
    //     ready: tryOnInstance.value && isTryOnCameraReady.value,
    //     pending: pendingSkuId.value
    //   },
    //   Date.now()
    // )
    if (tryOnInstance.value && isTryOnCameraReady.value && pendingSkuId.value) {
      // console.log('[[ Camera ] apply / --------------------------------')
      // console.log('[[ Camera ] apply / post item', {id: pendingSkuId.value}, Date.now())
      // isCameraError.value = false
      tryOnInstance.value.contentWindow?.postMessage({
        skuId: pendingSkuId.value
      })
      applyingSkuId.value = pendingSkuId.value
      pendingSkuId.value = undefined
      return true
    }
    return false
  }

  const setSkuId = (skuId: string) => {
    if (applyingSkuId.value) {
      if (skuId === applyingSkuId.value) {
        return
      }
      pendingSkuId.value = skuId
      return
    }
    if (currentSkuId.value) {
      if (skuId === currentSkuId.value) {
        return
      }
    }
    pendingSkuId.value = skuId
    const _posted = postSkuId('new set')
    // _posted && console.log('[[ Camera ] apply / posted after set', Date.now())
  }

  const setAppliedSkuId = (skuId: string) => {
    if (skuId === applyingSkuId.value) {
      isTryOnCameraError.value = false
      if (skuId !== currentSkuId.value) {
        currentSkuId.value = skuId
        if (!appliedSkuids.value.includes(currentSkuId.value)) {
          appliedSkuids.value = [...appliedSkuids.value, currentSkuId.value]
        }
      }
      applyingSkuId.value = undefined
      // console.log('[[ Camera ] apply / complete', {id: skuId}, Date.now())
      // console.log('[[ Camera ] apply / --------------------------------')
    }
    const _posted = postSkuId('applied')
    // _posted &&
    //   console.log('[[ Camera ] apply / posted after applied', Date.now())
  }

  const setTryOnCameraReady = () => {
    if (isTryOnCameraReady.value) return
    isTryOnCameraFailed.value = false
    isTryOnCameraIdle.value = false
    isTryOnCameraStopped.value = false
    if (!isTryOnCameraReady.value) {
      isTryOnCameraReady.value = debug || true
    }
    const _posted = postSkuId('ready')
    // _posted && console.log('[[ Camera ] apply / posted after ready', Date.now())
  }

  const hasRejectedEver = ref(false)
  const setTryOnCameraFailed = () => {
    if (isTryOnCameraFailed.value) return
    markFailed(0)
  }

  const setTryOnCameraError = () => {
    if (isTryOnCameraError.value) return
    isTryOnCameraError.value = true
  }

  const setDefaultCameraReady = () => {
    console.log('[[ Camera ] Set Default Ready ]')
    isDefaultCameraFailed.value = false
    isDefaultCameraReady.value = true
  }

  const onReceiveIframeMessage = async (event: MessageEvent) => {
    // if (event.data?.hello) return
    // console.log('[[ Camera ] receive / event ]', event)
    if (typeof event?.data?.type === 'string') {
      const { type, payload } = event.data
      switch (type) {
        case 'loaded':
          // console.log('[[ Camera ] receive / loaded ]', { camera: 'try-on' })
          return setTryOnCameraReady()

        case 'camera-failed':
          console.log('[[ Camera ] receive / load fail ]', { type })
          return setTryOnCameraFailed()

        case 'applied':
          // console.log('[[ Camera ] receive / applied callback ]', {
          //   id: payload
          // })
          return setAppliedSkuId(payload)

        case 'snapshot':
          console.log('[[ Camera ] receive / snapshot data ]')
          setSnapshotResult(payload)
          break

        case 'applied-failed':
        case 'snapshot-failed':
        case 'error-guid':
          console.log('[[ Camera ] receive / error ]', { type })
          setTryOnCameraError()
          break
      }
    }
  }

  const resetPartialStates = () => {
    isTryOnCameraIdle.value = true
    isTryOnCameraReady.value = debug || false
    isTryOnCameraError.value = false
    isTryOnCameraFailed.value = false
    isDefaultCameraFailed.value = false
    applyingSkuId.value = undefined
    currentSkuId.value = undefined
  }
  const resetStates = () => {
    console.log('[[ Camera ] ResetCameraFrameStore ]')
    cameraMode.value = 'none'
    setTryOnType()

    tryOnUrlVersion.value = Date.now()
    resetPartialStates()
    isDefaultCameraReady.value = false
  }

  // iframe 交互
  onMounted(() => window.addEventListener('message', onReceiveIframeMessage))
  onUnmounted(() =>
    window.removeEventListener('message', onReceiveIframeMessage)
  )

  return {
    tryOnInstance,
    defaultInstance,

    cameraMode,
    tryOnUrl,
    isTryOnCameraReady,
    isTryOnCameraMount,
    isDefaultCameraShown,
    isDefaultCameraActive,

    hasUnreadCameraFailed,
    isCameraLoading,
    hasUnreadCameraError: isTryOnCameraError,

    snapshotResult,

    currentSkuId,
    isSkuLoading,

    getUserMedia,
    setTryOnType,
    setSkuId,
    resetStates,
    resumeCamera,
    pauseCamera,
    stopCamera,
    startCamera,
    snapshot,
    onReceiveIframeMessage,
    setDefaultCameraReady
  }
})
